/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import cx from 'classnames';
import { FieldArray, FormikProps, getIn } from 'formik';
import { DateTime, WeekdayNumbers } from 'luxon';
import { useCallback, useEffect, useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import AddIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import RemoveIcon from '@mui/icons-material/RemoveCircleOutlineOutlined';
import {
  Button,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  OutlinedInput,
  Tooltip,
  Typography,
} from '@mui/material';
import { TimePicker } from '@mui/x-date-pickers';

import { ArrowRoundedIcon } from '../../../components/svg-icons/ArrowRoundedIcon';
import { HUAutocomplete } from '../../../elements/HUAutocomplete/HUAutocomplete';
import { destructRenderInputParams } from '../../../elements/HUTextInput/utils';
import { useFormStyles } from '../../../styles/form';
import { useMaintenanceWindowsConfigureStyles } from '../../../styles/maintenanceWindowsConfigure';
import { useUtcFormat } from '../../../utils/useUtcFormat';
import { useValidationTranslation } from '../../../utils/useValidationTranslation';
import { WeekdayMap } from '../CreateClusterOverlay.consts';
import { CreateClusterValues, Weekday } from '../CreateClusterOverlay.types';

type CreateClusterStepConfigureMaintenanceWindowsProps<Values> = FormikProps<Values> & object;

export function CreateClusterStepConfigureMaintenanceWindows<Values extends CreateClusterValues>(
  props: CreateClusterStepConfigureMaintenanceWindowsProps<Values>,
) {
  const { values, handleBlur, setFieldValue, setFieldTouched, errors, touched, validateForm } = props;

  const { t, i18n } = useTranslation();
  const { vtr } = useValidationTranslation();

  const classes = useMaintenanceWindowsConfigureStyles();
  const formClasses = useFormStyles();

  const { utcLocal } = useUtcFormat();

  useEffect(() => {
    void validateForm();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleWeekdayChange = (value: string, index: number) => {
    void setFieldValue(`maintenanceWindows.${index}.weekday`, value);
    void setFieldTouched('maintenanceWindows', true, false);
  };

  const getOptionLabel = useCallback(
    (option: string) =>
      option
        ? DateTime.fromObject(
            { weekday: WeekdayMap[option as Weekday] as WeekdayNumbers },
            { locale: i18n.language },
          ).toFormat('cccc')
        : t('createCluster:labels.configure_maintenance_windows.every_day'),
    [i18n, t],
  );

  const handleStartTimeChange = (value: DateTime | null, index: number) => {
    const field = `maintenanceWindows.${index}.startTimeUtc`;
    if (!value) {
      void setFieldValue(field, null);
    } else {
      void setFieldValue(field, value);
    }
    void setFieldTouched(field, true, false);
  };

  const handleEndTimeChange = (value: DateTime | null, index: number) => {
    const field = `maintenanceWindows.${index}.endTimeUtc`;
    if (!value) {
      void setFieldValue(field, null);
    } else {
      void setFieldValue(field, value);
    }
    void setFieldTouched(field, true, false);
  };

  const fieldHasError = (field: 'startTimeUtc' | 'endTimeUtc', index: number) => {
    const fieldFull = `maintenanceWindows.${index}.${field}`;
    const error = getIn(errors, fieldFull);
    const touchedField = getIn(touched, 'maintenanceWindows');
    return !!touchedField && !!error;
  };

  const fieldGetError = (field: 'startTimeUtc' | 'endTimeUtc', index: number) => {
    const fieldFull = `maintenanceWindows.${index}.${field}`;
    const error = getIn(errors, fieldFull);
    return error || '';
  };

  const isReachedMaxLimit = useMemo(() => values.maintenanceWindows.length === 6, [values.maintenanceWindows]);
  const isRemoveDisabled = useMemo(() => values.maintenanceWindows.length === 1, [values.maintenanceWindows]);

  const reachedMaxTitle = useMemo(
    () => (isReachedMaxLimit ? t('createCluster:labels.configure_maintenance_windows.max_limit') : ''),
    [isReachedMaxLimit, t],
  );

  const localMaintenanceWindowTime = useCallback(
    (index: number) => {
      const weekdayValue = values.maintenanceWindows[index].weekday;
      const weekday = weekdayValue
        ? DateTime.fromObject(
            { weekday: WeekdayMap[weekdayValue] as WeekdayNumbers },
            { locale: i18n.language },
          ).toFormat('cccc')
        : String(t('createCluster:labels.configure_maintenance_windows.every_day')).toLowerCase();

      const startValue = values.maintenanceWindows[index].startTimeUtc;
      const endValue = values.maintenanceWindows[index].endTimeUtc;

      const startTime =
        startValue && startValue.isValid
          ? values.maintenanceWindows[index].startTimeUtc.toLocal().toFormat('HH:mm')
          : '--:--';
      const endTime =
        endValue && endValue.isValid
          ? values.maintenanceWindows[index].endTimeUtc.toLocal().toFormat('HH:mm')
          : '--:--';

      return (
        <Trans
          i18nKey={`createCluster:labels.configure_maintenance_windows.local_time_hint`}
          components={{ b: <b /> }}
          values={{
            weekday,
            startTime,
            endTime,
            utcLocal,
          }}
        />
      );
    },
    [values.maintenanceWindows, i18n.language, t, utcLocal],
  );

  return (
    <>
      <div className={classes.heading}>{t('createCluster:parameters.configure_maintenance_windows')}</div>
      <FieldArray
        name="maintenanceWindows"
        render={helpers => (
          <div>
            {values.maintenanceWindows.map(
              ({ weekday: weekdayValue, startTimeUtc: startTimeUtcValue, endTimeUtc: endTimeUtcValue }, idx) => (
                <div key={idx}>
                  <Grid
                    item
                    classes={{ root: 'd-flex' }}
                    style={{ alignItems: 'baseline', gap: '10px', marginTop: '10px' }}
                  >
                    <FormControl
                      variant="outlined"
                      className={cx(formClasses.formControl, formClasses.widthDefault, classes.dayPicker)}
                    >
                      <InputLabel htmlFor={`maintenanceWindows.${idx}.weekday`}>
                        {t('createCluster:labels.configure_maintenance_windows.weekday')}
                      </InputLabel>
                      <HUAutocomplete
                        renderInput={renderParams => (
                          <OutlinedInput
                            {...destructRenderInputParams(renderParams)}
                            name={`maintenanceWindows.${idx}.weekday`}
                            label={t('createCluster:labels.configure_maintenance_windows.weekday')}
                          />
                        )}
                        popupIcon={<ArrowRoundedIcon />}
                        options={Object.keys(WeekdayMap)}
                        value={weekdayValue}
                        getOptionLabel={getOptionLabel}
                        onChange={(_e, value) => handleWeekdayChange(value ?? '', idx)}
                        onBlur={handleBlur}
                        disableClearable
                        data-cy={`create-cluster-maintenance-windows-weekday-${idx}`}
                      />
                    </FormControl>
                    <FormControl
                      className={cx(formClasses.formControl, formClasses.widthDefault, classes.timePicker)}
                      error={fieldHasError('startTimeUtc', idx)}
                      data-cy={`create-cluster-maintenance-windows-start-time-${idx}`}
                    >
                      <TimePicker
                        name={`maintenanceWindows.${idx}.startTimeUtc`}
                        ampm={false}
                        timezone="UTC"
                        label={t('createCluster:labels.configure_maintenance_windows.start_time')}
                        value={startTimeUtcValue}
                        onChange={value => handleStartTimeChange(value, idx)}
                        slotProps={{
                          textField: {
                            error: fieldHasError('startTimeUtc', idx),
                          },
                        }}
                      />
                      {fieldHasError('startTimeUtc', idx) && (
                        <FormHelperText data-cy={`create-cluster-maintenance-windows-start-time-error-${idx}`}>
                          {vtr(fieldGetError('startTimeUtc', idx), {
                            field: t('createCluster:labels.configure_maintenance_windows.start_time'),
                          })}
                        </FormHelperText>
                      )}
                    </FormControl>
                    &ndash;
                    <FormControl
                      className={cx(formClasses.formControl, formClasses.widthDefault, classes.timePicker)}
                      error={fieldHasError('endTimeUtc', idx)}
                      data-cy={`create-cluster-maintenance-windows-end-time-${idx}`}
                    >
                      <TimePicker
                        name={`maintenanceWindows.${idx}.endTimeUtc`}
                        ampm={false}
                        timezone="UTC"
                        label={t('createCluster:labels.configure_maintenance_windows.end_time')}
                        value={endTimeUtcValue}
                        onChange={value => handleEndTimeChange(value, idx)}
                        slotProps={{
                          textField: {
                            error: fieldHasError('endTimeUtc', idx),
                          },
                        }}
                      />
                      {fieldHasError('endTimeUtc', idx) && (
                        <FormHelperText data-cy={`create-cluster-maintenance-windows-end-time-error-${idx}`}>
                          {vtr(fieldGetError('endTimeUtc', idx), {
                            field: t('createCluster:labels.configure_maintenance_windows.end_time'),
                            differFromField: t('createCluster:labels.configure_maintenance_windows.start_time'),
                            laterThan: startTimeUtcValue.toFormat('HH:mm'),
                          })}
                        </FormHelperText>
                      )}
                    </FormControl>
                    <div>
                      <Tooltip title={t('createCluster:labels.configure_maintenance_windows.actions.remove')}>
                        <IconButton
                          onClick={() => helpers.remove(idx)}
                          disabled={isRemoveDisabled}
                          data-cy={`create-cluster-maintenance-windows-remove-${idx}`}
                        >
                          <RemoveIcon />
                        </IconButton>
                      </Tooltip>
                    </div>
                  </Grid>
                  <FormHelperText className={classes.hint}>{localMaintenanceWindowTime(idx)}</FormHelperText>
                </div>
              ),
            )}
            <Tooltip title={reachedMaxTitle} sx={{ textAlign: 'center' }}>
              <span>
                <Button
                  onClick={() => {
                    helpers.push({
                      weekday: '',
                      startTimeUtc: DateTime.fromObject({ hour: 0, minute: 0 }, { zone: 'utc' }),
                      endTimeUtc: DateTime.fromObject({ hour: 5, minute: 0 }, { zone: 'utc' }),
                    });
                    void setFieldTouched('maintenanceWindows', true);
                    void validateForm();
                  }}
                  sx={{ mt: '5px' }}
                  startIcon={<AddIcon />}
                  disabled={isReachedMaxLimit}
                  data-cy="create-cluster-maintenance-windows-add"
                >
                  {t('createCluster:labels.configure_maintenance_windows.actions.add')}
                </Button>
              </span>
            </Tooltip>
          </div>
        )}
      />
      {typeof errors.maintenanceWindows === 'string' && (
        <Typography
          className={cx(classes.error, formClasses.widthMedium)}
          data-cy="create-cluster-maintenance-windows-error"
        >
          {t(`createCluster:validation.maintenance_windows.${errors.maintenanceWindows}`, {
            maxLength: 6,
          })}
        </Typography>
      )}
    </>
  );
}
